import type { NavigationHookAfter } from 'vue-router';
import { computed, onBeforeMount, onBeforeUnmount, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { GA_EVENTS } from '@/shared/analytics';
import { useCommonService } from '@/shared/api';
import { usePushEvent, useRtl } from '@/shared/composables';
import { initRecaptcha } from '@/shared/lib/recaptcha';
import { normalizePath } from '@/shared/utils/vue-router';
import { useABDistribution, useABTestStore } from '../../ABTesting';
import { useFlowStore } from '../model/Flow.store';
import { useFlowRouter } from './useFlowRouter';

export function useFlowInitialization(externalHooks: { onBeforeMount?: () => Promise<void> } = {}) {
  const router = useRouter();
  const route = useRoute();
  let unregisterRouterHook = () => {};
  const pushEvent = usePushEvent();
  const flowRouter = useFlowRouter();
  const flowStore = useFlowStore();
  const abStore = useABTestStore();
  const { isRtl } = useRtl();

  const withFeedback = computed(() => Boolean(route.meta.withFeedback));
  const showArrowBack = computed(
    () =>
      !flowStore.state.isUpgradeTplComprehensiveFlow
      && !flowRouter.routeMeta.value.isExceptionPage
      && !flowRouter.routeMeta.value.isResultPage,
  );

  onBeforeUnmount(() => {
    unregisterRouterHook();
  });

  watch(
    () => route.path,
    () => {
      flowStore.$update({
        isExceptionPage: !!flowRouter.routeMeta.value.isExceptionPage,
        isResultPage: !!flowRouter.routeMeta.value.isResultPage,
      });
    },
    {
      immediate: true,
    },
  );

  async function checkRootFlowAndRedirectToFirstStep() {
    const normalizedPath = normalizePath(route.path);
    if (normalizedPath === flowRouter.pathHead) {
      await flowRouter.goToFirstStep();
    }
  }

  onBeforeMount(async () => {
    if (!abStore.abDistributionRequestResolved) {
      await useABDistribution().loadABDistribution();
    }

    await externalHooks.onBeforeMount?.();

    const isCore = abStore.isCoreExperiment;
    useCommonService().setHeader('Api-Provider', isCore ? 'core' : 'anoud');
    useCommonService().setHeader('Locale', isRtl.value ? 'ar' : 'en');

    if (isCore) {
      initRecaptcha();
    }

    flowStore.checkAndSetPromoCode();

    flowStore.checkUpgradeTplComprehensiveFlow();

    await flowStore.checkEntryPointAndClearIt();

    flowStore.checkAndSetCampaignOffer();

    await checkRootFlowAndRedirectToFirstStep();

    const pushEventRouterHook: NavigationHookAfter = async (_, from) => {
      await pushEvent(GA_EVENTS.VIEW_PAGE, from);
    };

    unregisterRouterHook = router.afterEach(pushEventRouterHook);
  });

  return {
    withFeedback,
    showArrowBack,
  };
}
